import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import '../styles/dashboard-light.css';

const STORAGE_KEY = 'snagcart-ui-theme';
/** Set true when light mode UI is ready to ship again. */
const LIGHT_THEME_ENABLED = false;

const UiThemeContext = createContext({
  theme: 'dark',
  isLight: false,
  setTheme: () => {},
  toggleTheme: () => {},
});

function readStoredTheme() {
  if (!LIGHT_THEME_ENABLED) return 'dark';
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch (_) { /* ignore */ }
  return 'dark';
}

export function UiThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readStoredTheme);

  useEffect(() => {
    const effective = LIGHT_THEME_ENABLED ? theme : 'dark';
    document.documentElement.setAttribute('data-ui-theme', effective);
    try {
      if (LIGHT_THEME_ENABLED) {
        localStorage.setItem(STORAGE_KEY, effective);
      } else {
        localStorage.setItem(STORAGE_KEY, 'dark');
      }
    } catch (_) { /* ignore */ }
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (!LIGHT_THEME_ENABLED) {
      setThemeState('dark');
      return;
    }
    setThemeState(next === 'light' ? 'light' : 'dark');
  }, []);

  const toggleTheme = useCallback(() => {
    if (!LIGHT_THEME_ENABLED) return;
    setThemeState((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  const effectiveTheme = LIGHT_THEME_ENABLED ? theme : 'dark';
  const value = useMemo(
    () => ({
      theme: effectiveTheme,
      isLight: LIGHT_THEME_ENABLED && effectiveTheme === 'light',
      setTheme,
      toggleTheme,
    }),
    [effectiveTheme, setTheme, toggleTheme],
  );

  return <UiThemeContext.Provider value={value}>{children}</UiThemeContext.Provider>;
}

export function useUiTheme() {
  return useContext(UiThemeContext);
}
